import {Injectable} from '@angular/core';
import {APIResponse, EncryptService, HttpService} from '../../core';
import {Observable, of} from 'rxjs';
import {map, mergeMap} from 'rxjs/operators';
import {Role, UserData} from '../domain/user.domain';
import {ToastrService} from 'ngx-toastr';
import {ProjectService} from './project.service';


export enum USERS_ROLES {
  DEV_ADMIN = 3,
  DEV_MANAGER = 4,
  DEV_EMPLOYEE = 5
}

/**
 * @class UserService
 * @description This is a service which provide user related method such as login, logout, getCurrentUser
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  private _currentUser: UserData;

  private _sysRoles: Array<Role>;

  constructor(
    private httpService: HttpService,
    private encryptService: EncryptService,
    private toastrService: ToastrService,
    private projectService: ProjectService
  ) {
  }

  getCurrentUser() {
    return this._currentUser;
  }

  set credit(value: number) {
    this._currentUser.credit = value;
  }

  loadSysRoles() {
    console.log('loadSysRoles');
    return this.httpService.searchRoles().pipe(map((res: any) => {
      console.log('loadSysRoles', res);
      if (res.code === 1) {
        console.log('sysrole', res.data);
        this._sysRoles = res.data;
      } else {
        this._sysRoles = null;
      }
    }));
  }


  login(fd: FormData): Observable<APIResponse> {
    console.log('cognito login');
    return this.httpService.cognitoLogin(fd).pipe(
        mergeMap((resFromLogin: any) => {
          console.log(resFromLogin);
          if (resFromLogin) {
            this._currentUser = resFromLogin;
            localStorage.setItem('id_token', resFromLogin.id_token);
            return this.getCryptoKey();
          } else {
            return of({ code: -4, data: this._currentUser });
          }
        })
    );
  }

  getCryptoKey() {
    return this.httpService.getCryptoKey().pipe(
        map(cryptoKey => {
          if (cryptoKey) {
            localStorage.setItem(
                this.encryptService.encryptUsingAES128('YHSESSIONID', cryptoKey),
                this.encryptService.encryptUsingAES128(this._currentUser.YHSESSIONID, cryptoKey)
            );
          }
          return { code: 1, data: this._currentUser };
        })
    );
  }

  getInfo() {
    return this.httpService.getInfo().pipe(map((res1: any) => {
      console.log(res1.data, 'res1.data');
      this._currentUser = res1.data;
      this._currentUser.email = this.getEmailFromContacts(res1.data.contacts);
      this._currentUser.phoneNumber = this.getMobileFromContacts(res1.data.contacts);
      this._currentUser.idOrigin = this.getIdOrigin(res1.data.contacts, res1.data.passwordContactItem.id);
      this._currentUser.roles.sort((role1: Role, role2: Role) => role2.weight - role1.weight);
      return { code: 1, data: this._currentUser };
    }));
  }


  getUsersByCompanyAndId(companyId, userId) {
    if (this._currentUser.roles[0]?.id !== 4) {
      return this.httpService.getAllUsersByCompany(companyId).pipe(
        map(res => {
          return res.data.filter((user) => {
            return user.id === userId;
          });
        })
      );
    }
  }

  // 按照角色将用户分组
  getAllUsersByCompanyWithClassification() {
    const dataSourceForManager = [];
    const dataSourceForOperator = [];
    return this.httpService.getAllUsersByCompany().pipe(
      map(res => {
        console.log(res.data);
        if (res.code === 1) {
          res.data.forEach((user) => {
            user.roles.forEach(role => {
              console.log(role);
              user.email = this.getEmailFromContacts(user.contacts);
              user.phoneNumber = this.getMobileFromContacts(user.contacts);
              if (role.role === 'dev-manager') {
                dataSourceForManager.push(user);
                console.log(dataSourceForManager);
              }
              if (role.role === 'dev-employee') {
                dataSourceForOperator.push(user);
                console.log(dataSourceForOperator);
              }
            });
          });
          return {
            manager: dataSourceForManager,
            operator: dataSourceForOperator
          };
        } else {
          return {
            manager: [],
            operator: []
          };
        }

      })
    );
  }

  logOut() {
    return this.httpService.userLogout().pipe(
      map((res: any) => {
        if (res.code === 1) {
          this._currentUser = null;
          localStorage.clear();
          this.projectService.projects = null;
          console.log('LogOut successfully');
        }
        return res;
      })
    );
  }

  quietLogin(YHSESSIONID: string): Observable<UserData> {
    const fd = new FormData();
    // return null;
    fd.append('sessionId', YHSESSIONID);
    return this.httpService.reLogin().pipe(
      mergeMap((res: any) => {
        if (res.code === 1) {
          this._currentUser = res.data;
          this._currentUser.email = this.getEmailFromContacts(res.data.contacts);
          this._currentUser.phoneNumber = this.getMobileFromContacts(res.data.contacts);
          this._currentUser.idOrigin = this.getIdOrigin(res.data.contacts, res.data.passwordContactItem.id);
          this._currentUser.roles.sort((role1: Role, role2: Role) => role2.weight - role1.weight);
          return of(this._currentUser);
        } else {
          return of(null);
        }
      }),
    );
  }

  get currentUser(): Observable<UserData> {
    if (this._currentUser) {
      return of(this._currentUser);
    } else {
      return this.httpService.getCryptoKey().pipe(
        mergeMap(cryptoKey => {
          const key = this.encryptService.encryptUsingAES128('YHSESSIONID', cryptoKey);
          if (localStorage.getItem(key)) {
            // console.log('sss');
            const YHSESSIONID = this.encryptService.decryptUsingAES128(localStorage.getItem(key), cryptoKey);
            return this.quietLogin(YHSESSIONID);
          } else {
            return of(null);
          }
        })
      );
    }
  }

  getEmailFromContacts(contacts) {
    const email = contacts.filter((c: any) => c.contactItem.id === 1)[0].content;
    // return the email of the user
    return email ? email : '';
  }

  getMobileFromContacts(contacts) {
    console.log(contacts);
    const phoneNumber = contacts.filter((c: any) => c.contactItem.id === 2);
    // return the mobile number of the user
    return phoneNumber[0] ? phoneNumber[0].content : '';
  }

  getIdOrigin(contacts, passwordContactItemID) {
    console.log(contacts, passwordContactItemID);
    return contacts.filter(item => item.contactItem.id === passwordContactItemID)[0].content;
  }
}
